import React, { useState, useRef, useImperativeHandle } from 'react';
import { createComponent, css, styled, Icon, Box } from '@goodrx/matisse';
import { Theme } from 'app/types/Theme';
import { useToggledState } from 'app/hooks';

const StyledInput = createComponent<Record<string, unknown>, 'input'>({
  name: 'Input',
  tag: 'input',
  style: ({ theme, borderRadius = theme.radius }: { theme: Theme; borderRadius: number }) => css`
    height: 40px;
    display: block;
    outline: none;
    width: 100%;
    padding: 8px;
    padding-right: 24px;
    border-radius: ${borderRadius}px;
    transition: 250ms all;
    -webkit-appearance: none;
    font-family: inherit;
    font-size: ${theme.typography.fontSize}px;
    color: ${theme.colors.greyDarkest};
    caret-color: ${theme.colors.black};
    box-sizing: border-box;
    background-color: ${theme.colors.greyLighter};
    border: none;
    padding-left: 28px;

    ::placeholder {
      color: ${theme.colors.greyDark};
    }

    &[disabled] {
      background-color: ${theme.colors.white};
      border-color: ${theme.colors.greyLight};
      color: ${theme.colors.grey};
      ::placeholder {
        color: ${theme.colors.grey};
      }
    }
  `,
});

const BaseIcon = styled(Icon)<{ isFocused: boolean }>`
  ${({ theme, isFocused }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    transition: 250ms all;
    cursor: pointer;
    color: ${theme.colors.greyDark};

    ${isFocused &&
    css`
      color: ${theme.colors.grey};
    `}
  `}
`;

const StyledLeftIcon = styled(BaseIcon)`
  left: 8px;
  margin-right: 8px;
`;

const StyledRightIcon = styled(BaseIcon)`
  right: 8px;
`;

type Props = {
  onChange(name: string, value: string): void;
  onClear?(): void;
  value?: string;
  name: string;
} & Omit<JSX.IntrinsicElements['input'], 'onChange'>;

export type SearchInputRefAttributes = {
  focus: () => void;
  clear: () => void;
};

export const SearchInput = React.forwardRef<SearchInputRefAttributes, Props>(
  ({ onChange, onClear, name, ...props }, ref) => {
    const [value, setValue] = useState(props.value || '');
    const [focused, toggleFocused] = useToggledState(false);

    const handleChange = (search: string) => {
      setValue(search);
      onChange(name, search);
    };

    useImperativeHandle(ref, () => ({
      focus: inputRef.current!.focus,
      clear: handleClear,
    }));

    const inputRef = useRef<typeof StyledInput & { focus: () => void }>();

    const handleClear = () => {
      setValue('');
      // eslint-disable-next-line no-unused-expressions
      onClear?.();
      // eslint-disable-next-line no-unused-expressions
      inputRef.current?.focus();
    };

    return (
      <Box position="relative" flex={1}>
        <StyledLeftIcon name="magnify" isFocused={focused} />
        <StyledInput
          {...props}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
          onFocus={toggleFocused}
          onBlur={toggleFocused}
          value={value}
          // @ts-ignore
          ref={inputRef}
        />
        {value && <StyledRightIcon name="close-circle" isFocused={focused} onClick={handleClear} />}
      </Box>
    );
  }
);
